import {BeneficiariesRouteConstants} from 'Beneficiaries/Routes';
import {BOSToolsRouteConstants} from 'BOSTools/Routes';
import DashboardCard from 'Core/components/Dashboard/Card';
import 'Core/components/Dashboard/Dashboard.sass';
import {showModal} from 'Core/ducks/modal';
import {SubscriptionsRouteConstants} from 'GenericTexting/Routes';
import React from 'react';
import {connect, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import {hasScope} from 'Shared/components/RequireScope';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {Scope} from 'Shared/constants/Scope';
import {isAgencyInStatus, selectUserScopes, loadUserClaims} from 'Users/selectors/userProfileSelectors';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import 'Core/components/Dashboard/Dashboard.sass';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {EnrollTrackClaims, UserTypeClaimKey} from 'Users/models/Claims';
import {AsyncUserClaims, UserTypeClaims} from 'Users/models/Claims';
import * as O from 'fp-ts/lib/Option';
import {matchUserTypeClaimKey} from 'Users/models/User/UserType';
import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import {AgentClaims} from 'Users/models/AgentClaims';
import Grid from '@material-ui/core/Grid';
import {UserRouteConstants} from 'Users/Routes';
import {CustomerSupportRouteConstants} from 'CustomerSupport/Routes';
import SortUtility from 'Shared/utilities/SortUtility';

type LineOfBusinessDashboardProps = {
  showModal: typeof showModal;
} & ReturnType<typeof mapStateToProps> & {userClaims: AsyncUserClaims} & RouteComponentProps;

export const hasScopeToViewEtsAdminDashboard = (scopes: Scope[]) => {
  return scopes.some(scope => scope === Scope.ViewETSAdminDashboard);
};

export const hasScopeToBrokerPrivilegesDashboard = (scopes: Scope[]) => {
  return scopes.some(scope => scope === Scope.SearchFileUpload);
};

export const hasScopeToViewEnrolltrackCustomerSupport = (scopes: Scope[]) => {
  return scopes.some(scope => scope === Scope.EnrollTrackSearchCustomerSupport);
};

export const LineOfBusinessDashboard = (props: LineOfBusinessDashboardProps) => {
  const {userClaims, history, scopes} = props;
  const userHasScope = hasScope(scopes);
  const claims = loadUserClaims(userClaims);

  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const hasBrokerPrivileges = userHasScope(Scope.SearchFileUpload);
  const isAgentAdmin = userHasScope(Scope.SearchAgentUsers) && !O.isSome(selectedAgency);
  const hasScopeForCustomerSupport =
    userHasScope(Scope.AgentSearchCustomerSupport) ||
    (userHasScope(Scope.EnrollTrackSearchCustomerSupport) && userHasScope(Scope.ImpersonateEnrollTrack));
  let [isImpersonatingEnrollTrackUser, isImpersonatingAgentUser] = [false, false];

  let [hasAgencyAssociation, hasAtLeastOneEnrollTrackScope] = [true, true];
  let [canViewBrokerPrivileges, canViewEtsAdminDashboard] = [false, false];
  let userRoleCount = 0;

  claims.userTypes.map(
    UserTypeClaims.match({
      [UserTypeClaimKey.AgentSite]: (agentClaims: AgentClaims) => {
        if (agentClaims.impersonatedAgentClaim) {
          isImpersonatingAgentUser = true;
          hasAgencyAssociation = agentClaims.impersonatedAgentClaim.agencyAssociations.length > 0;
        }
      },

      [UserTypeClaimKey.EnrollTrack]: (enrollTrackClaims: EnrollTrackClaims) => {
        if (enrollTrackClaims.impersonatedEnrollTrackClaim) {
          isImpersonatingEnrollTrackUser = true;
          hasAtLeastOneEnrollTrackScope = enrollTrackClaims.impersonatedEnrollTrackClaim.scopes.length > 0;
          canViewBrokerPrivileges = hasScopeToBrokerPrivilegesDashboard(
            enrollTrackClaims.impersonatedEnrollTrackClaim.scopes
          );
          canViewEtsAdminDashboard = hasScopeToViewEtsAdminDashboard(
            enrollTrackClaims.impersonatedEnrollTrackClaim.scopes
          );
        }
      },
      default: () => false,
    })
  );

  claims.userTypes.map(
    UserTypeClaims.match({
      [UserTypeClaimKey.AgentSite]: (agentClaims: AgentClaims) => {
        process.env.REACT_APP_TOGGLE_AGENT_ACCESS === 'true' ? userRoleCount++ : userRoleCount--;
      },
      default: () => userRoleCount++,
    })
  );
  const isImpersonating = isImpersonatingEnrollTrackUser || isImpersonatingAgentUser;
  const isImpersonatingEnrollTrackAndAgentUser = isImpersonatingEnrollTrackUser && isImpersonatingAgentUser;
  const userTypeCount = claims.userTypes.length;

  const handleAgentDashboardTileClick = () => {
    if (isAgentAdmin) {
      history.push(AgencyAdministrationRouteConstants.AGENTS);
    } else {
      history.push('/agent/agentDashboard');
    }
  };

  const handleEnrollTrackDashboardTileClick = () => {
    if (isImpersonatingEnrollTrackUser) {
      history.push(
        canViewBrokerPrivileges
          ? '/enrollment/externalEnrollTrackDashboard'
          : canViewEtsAdminDashboard
          ? EnrollmentRouteConstants.ENROLLMENTS_ETS_ADMIN_DASHBOARD
          : EnrollmentRouteConstants.ENROLLMENTS_DASHBOARD
      );
    } else {
      history.push(
        hasBrokerPrivileges
          ? '/enrollment/externalEnrollTrackDashboard'
          : hasScopeToViewEtsAdminDashboard(scopes)
          ? EnrollmentRouteConstants.ENROLLMENTS_ETS_ADMIN_DASHBOARD
          : EnrollmentRouteConstants.ENROLLMENTS_DASHBOARD
      );
    }
  };

  const handlePortalDashboardTileClick = () => {
    history.push(UserRouteConstants.USERS);
  };

  const lineOfBusinessDashboardCards = claims.userTypes.map(userType =>
    matchUserTypeClaimKey(userType, {
      [UserTypeClaimKey.ApplicationEntry]: () =>
        !isImpersonating ? (
          <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.ApplicationEntry}`}>
            <DashboardCard
              key={UserTypeClaimKey.ApplicationEntry}
              image={() => <i className="fa fa-file-invoice" />}
              title="Application Entry"
              description="Quickly and easily enter new applications in the MyAccess Portal."
              linkText="Enter Applications"
              onClickLink={() => {
                history.push(ApplicationEntryRouteConstants.WorkQueue);
              }}
            />
          </Grid>
        ) : (
          <></>
        ),
        [UserTypeClaimKey.AgentSite]: () => <></>,
      // [UserTypeClaimKey.AgentSite]: () => {
      //   if (
      //     process.env.REACT_APP_TOGGLE_AGENT_ACCESS === 'true' &&
      //     ((isImpersonatingEnrollTrackAndAgentUser && hasAgencyAssociation) || (!isImpersonating && userTypeCount > 1))
      //   ) {
      //     return (
      //       <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.AgentSite}`}>
      //         <DashboardCard
      //           key={UserTypeClaimKey.AgentSite}
      //           image={() => <i className="fa fa-briefcase" />}
      //           title={`Agent${isAgentAdmin ? ' Administration' : ''}`}
      //           description={`Access${isAgentAdmin ? ' features for agent administration.' : ' broker resources.'}`}
      //           linkText="Agent"
      //           onClickLink={handleAgentDashboardTileClick}
      //         />
      //       </Grid>
      //     );
      //   } else {
      //     process.env.REACT_APP_TOGGLE_AGENT_ACCESS === 'true' &&
      //       !isImpersonatingEnrollTrackUser &&
      //       handleAgentDashboardTileClick();
      //     return <></>;
      //   }
      // },

      [UserTypeClaimKey.Messaging]: () => <></>,

      // [UserTypeClaimKey.Messaging]: () =>
      //   !isImpersonating ? (
      //     <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.Messaging}`}>
      //       <DashboardCard
      //         key={UserTypeClaimKey.Messaging}
      //         image={() => <i className="fa fa-envelope" />}
      //         title="Messaging"
      //         description="Send messages to phone numbers of a subscriber."
      //         linkText="Messaging"
      //         onClickLink={() => {
      //           history.push(SubscriptionsRouteConstants.MESSAGES);
      //         }}
      //       />
      //     </Grid>
      //   ) : (
      //     <></>
      //   ),
      [UserTypeClaimKey.MyAccessPortal]: () => {
        if (!isImpersonating) {
          if (userTypeCount > 1) {
            return (
              <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.MyAccessPortal}`}>
                <DashboardCard
                  key={UserTypeClaimKey.MyAccessPortal}
                  image={() => <i className="fas fa-fingerprint" />}
                  title="Portal"
                  description="Administer security in the MyAccess portal."
                  linkText="Enter IAM"
                  onClickLink={handlePortalDashboardTileClick}
                />
              </Grid>
            );
          } else {
            handlePortalDashboardTileClick();

            return <></>;
          }
        } else {
          return <></>;
        }
      },

      [UserTypeClaimKey.BOSTools]: () =>
        !isImpersonating ? (
          <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.BOSTools}`}>
            <DashboardCard
              key={UserTypeClaimKey.BOSTools}
              image={() => <i className="fa fa-cog" />}
              title="BOS Tools"
              description="Use BOS Tools."
              linkText="BOS Tools"
              onClickLink={() => {
                history.push(BOSToolsRouteConstants.BOSTOOLS);
              }}
            />
          </Grid>
        ) : (
          <></>
        ),
      [UserTypeClaimKey.Beneficiary]: () =>
        !isImpersonating ? (
          <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.Beneficiary}`}>
            <DashboardCard
              key={UserTypeClaimKey.Beneficiary}
              image={() => <i className="fa fa-hand-holding-usd" />}
              title="Beneficiaries"
              description="View and Update Beneficiaries."
              linkText="View Beneficiaries"
              onClickLink={() => {
                history.push(BeneficiariesRouteConstants.BENEFICIARIES);
              }}
            />
          </Grid>
        ) : (
          <></>
        ),
        [UserTypeClaimKey.EnrollTrack]: () => <></>,
      // [UserTypeClaimKey.EnrollTrack]: () => {
      //   if (hasAtLeastOneEnrollTrackScope && userRoleCount > 1) {
      //     return (
      //       <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-${UserTypeClaimKey.EnrollTrack}`}>
      //         <DashboardCard
      //           key={UserTypeClaimKey.EnrollTrack}
      //           image={() => <i className="fa fa-file" />}
      //           title="EnrollTrack"
      //           description="Track enrollments."
      //           linkText="EnrollTrack"
      //           onClickLink={() => {
      //             history.push(
      //               hasBrokerPrivileges
      //                 ? '/enrollment/externalEnrollTrackDashboard'
      //                 : hasScopeToViewEtsAdminDashboard(scopes)
      //                 ? EnrollmentRouteConstants.ENROLLMENTS_ETS_ADMIN_DASHBOARD
      //                 : EnrollmentRouteConstants.ENROLLMENTS_DASHBOARD
      //             );
      //           }}
      //         />
      //       </Grid>
      //     );
      //   } else {
      //     handleEnrollTrackDashboardTileClick();
      //     return <></>;
      //   }
      // },
      Empty: () => <></>,
    })
  );

  const isUserAdmin =
    !isImpersonating &&
    lineOfBusinessDashboardCards.filter(
      dashboardCard => dashboardCard.key === `grid-${UserTypeClaimKey.MyAccessPortal}`
    ).length === 0 &&
    userHasScope(Scope.SearchPortalUsers);

  const userAdministrationTile = isUserAdmin && (
    <Grid item lg={3} md={4} sm={6} xs={12} key={`grid-user-administration`}>
      <DashboardCard
        key={'user-administration'}
        image={() => <i className="fas fa-users" />}
        title="User Administration"
        description="Maintain users."
        linkText="User Administration"
        onClickLink={handlePortalDashboardTileClick}
      />
    </Grid>
  );

  // const isEnrolltrackCustomerSupport =
  //   !isImpersonating &&
  //   hasScopeForCustomerSupport &&
  //   (isAgentAdmin || hasScopeToViewEnrolltrackCustomerSupport(scopes));
  // const customerSupportTile = isEnrolltrackCustomerSupport && (
  //   <Grid
  //     item
  //     lg={3}
  //     md={4}
  //     sm={6}
  //     xs={12}
  //     key={`grid-${
  //       userHasScope(Scope.EnrollTrackSearchCustomerSupport) ? UserTypeClaimKey.EnrollTrack : UserTypeClaimKey.AgentSite
  //     }`}
  //   >
  //     <DashboardCard
  //       key={'Customer Support'}
  //       image={() => <i className="fas fa-headset" />}
  //       title="Customer Support"
  //       description="Access features for customer support administration"
  //       linkText="Customer Support"
  //       onClickLink={() => {
  //         props.history.push(CustomerSupportRouteConstants.CUSTOMER_SUPPORT);
  //       }}
  //     />
  //   </Grid>
  // );
  const NoAccess = (
    <Grid container spacing={0} direction="column" alignItems="center" justify="center">
      <Grid item lg={3}>
        You are not authorized to access content on this website.
      </Grid>
    </Grid>
  );
  const tileCount =
    lineOfBusinessDashboardCards.filter(dashboardCard => dashboardCard.key).length +
    // (customerSupportTile ? 1 : 0) +
    (userAdministrationTile ? 1 : 0);

  const orderedLineOfBusinessDashboardCards = SortUtility.sortElementsByKey(lineOfBusinessDashboardCards);
  return (
    <div className="dashboard dashboard-lineOfBusiness">
      <Grid container spacing={4}>
        {tileCount === 0 ? NoAccess : orderedLineOfBusinessDashboardCards}
        {userAdministrationTile}
        {/* {customerSupportTile} */}
      </Grid>
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  scopes: selectUserScopes(state),
  isTerminatedAgent: isAgencyInStatus(state, AgentNumberStatusId.Terminated),
});

export default withRouter(
  connect(mapStateToProps, {
    showModal,
  })(LineOfBusinessDashboard)
);
